// src/Pages/Assets/futuresComponentes/HistoryCard.jsx
import React from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { Box, Chip, Grid, Paper, Typography } from "@mui/material";
import { fmt, GREEN, RED, toDateStr } from "./pnlUtils";

export default function HistoryCard({ row }) {
  const theme = useTheme();
  const borderCol = alpha(theme.palette.divider, 0.6);
  const posColor = row.position === "Long" ? GREEN : RED;
  const pnl = row._realized != null ? row._realized : null;
  const roe = row._roe != null ? row._roe : null;

  return (
    <Paper
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${borderCol}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[1],
      }}
    >
      {/* Header */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
          {row._symbol}
        </Typography>
        <Chip
          size="small"
          label={row.position}
          sx={{
            height: 24,
            borderRadius: 1.5,
            fontWeight: 700,
            bgcolor: alpha(posColor, 0.15),
            color: posColor,
          }}
        />
        <Chip
          size="small"
          label={row.margin_type || "—"}
          sx={{ height: 24, borderRadius: 1.5, fontWeight: 700 }}
        />
        <Chip
          size="small"
          label={Number.isFinite(row.leverage) ? `${row.leverage}x` : "-"}
          sx={{ height: 24, borderRadius: 1.5, fontWeight: 700 }}
        />
        <Chip
          size="small"
          label={row.order_type || "—"}
          sx={{ height: 24, borderRadius: 1.5, fontWeight: 700 }}
        />
        <Chip
          size="small"
          label={row.status}
          sx={{ height: 24, borderRadius: 1.5, fontWeight: 700 }}
        />
        <Box sx={{ ml: "auto" }}>
          {pnl != null && (
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 900, color: pnl >= 0 ? GREEN : RED }}
            >
              {fmt(pnl, 2)} USDT {roe != null ? `• ${fmt(roe, 2)}%` : ""}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Body */}
      <Grid container spacing={1.2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2 }}>
            <Row k="Amount" v={fmt(row.amount ?? row.qty, 4)} />
            <Row
              k="Open"
              v={fmt(
                row.open_price ?? row.avg_open_price ?? row.entry_price,
                2
              )}
            />
            <Row
              k="Close"
              v={fmt(
                row.close_price ??
                  row.executed_close_price ??
                  row.exit_price ??
                  row.tp_hit_price ??
                  row.sl_hit_price,
                2
              )}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2 }}>
            <Row k="Margin" v={row.margin_type || "-"} />
            <Row
              k="Leverage"
              v={Number.isFinite(row.leverage) ? `${row.leverage}x` : "-"}
            />
            <Row k="Date" v={toDateStr(row.created_at || row.updated_at)} />
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}

function Row({ k, v }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 1,
        fontSize: 12,
        opacity: 0.9,
      }}
    >
      <Typography variant="caption" sx={{ opacity: 0.75 }}>
        {k}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {v}
      </Typography>
    </Box>
  );
}
